import React, { createContext, useContext, useState } from 'react';

// This is the legacy theme context - keeping for backward compatibility
type GlassMorphismLevel = 'none' | 'light' | 'medium' | 'heavy';

interface ThemeContextType {
  glassMorphism: GlassMorphismLevel;
  setGlassMorphism: (level: GlassMorphismLevel) => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
  useGradients: boolean;
  setUseGradients: (use: boolean) => void;
  useGlow: boolean;
  setUseGlow: (use: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [glassMorphism, setGlassMorphism] = useState<GlassMorphismLevel>('medium');
  const [accentColor, setAccentColor] = useState<string>('primary');
  const [useGradients, setUseGradients] = useState<boolean>(true);
  const [useGlow, setUseGlow] = useState<boolean>(true);

  return (
    <ThemeContext.Provider
      value={{
        glassMorphism,
        setGlassMorphism,
        accentColor,
        setAccentColor,
        useGradients,
        setUseGradients,
        useGlow,
        setUseGlow,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    // Provide fallback values instead of throwing error
    console.warn('useThemeContext used outside of ThemeProvider, using defaults');
    return {
      glassMorphism: 'medium' as GlassMorphismLevel,
      setGlassMorphism: () => {},
      accentColor: 'primary',
      setAccentColor: () => {},
      useGradients: true,
      setUseGradients: () => {},
      useGlow: true,
      setUseGlow: () => {},
    };
  }
  return context;
};

// Helper function to get glass morphism classes - always use dark mode
export const getGlassMorphismClasses = (level: GlassMorphismLevel): string => {
  const baseClasses = 'transition-all duration-300 border';
  
  switch (level) {
    case 'none':
      return `${baseClasses} bg-background border-border`;
    case 'light':
      return `${baseClasses} bg-black/10 backdrop-blur-sm border-white/10`;
    case 'medium':
      return `${baseClasses} bg-black/30 backdrop-blur-md border-white/20 shadow-lg`;
    case 'heavy':
      return `${baseClasses} bg-black/40 backdrop-blur-xl border-white/20 shadow-xl`;
    default:
      return baseClasses;
  }
};

// Helper to get glow effect classes - always use dark mode
export const getGlowClasses = (useGlow: boolean, color: string = 'primary'): string => {
  if (!useGlow) return '';
  
  return `shadow-[0_0_15px_rgba(var(--${color})/0.3)] hover:shadow-[0_0_25px_rgba(var(--${color})/0.4)]`;
};

// Helper to get gradient classes - always use dark mode
export const getGradientClasses = (useGradients: boolean, type: 'card' | 'button' | 'slider' = 'card'): string => {
  if (!useGradients) return '';
  
  switch (type) {
    case 'card':
      return 'bg-gradient-to-br from-background to-black/40';
    case 'button':
      return 'bg-gradient-to-r from-primary to-primary/80';
    case 'slider':
      return 'bg-gradient-to-r from-primary to-primary/80';
    default:
      return '';
  }
};
